import React, { useState, useEffect, useCallback } from "react";
import DataTable from "../../../../Pages/InputField/DataTable";
import { Modal, Button, Table, Row, Col } from "react-bootstrap";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import baseURL from "../../../../../Url/NodeBaseURL";
import { BlobProvider } from "@react-pdf/renderer";
import EstimateA4 from "../EstimateA4";

const EstimateTableV2 = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [directPrintData, setDirectPrintData] = useState(null); // data for direct printing
  const [urdPurchaseData, setUrdPurchaseData] = useState([]); // URD purchase data for modal
  const [urdPurchaseAmount, setUrdPurchaseAmount] = useState(0); // Total URD purchase amount
  const [salesReturnData, setSalesReturnData] = useState([]); // Sales return data for modal
  const [salesReturnAmount, setSalesReturnAmount] = useState(0); // Total sales return amount
  const [overlayLoading, setOverlayLoading] = useState(false); // Overlay loading state for PDF generation

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${baseURL}/Estimate/summary`);
      
      // First, create basic table rows from summary data
      const basicTableRows = (res.data.summary || [])
        .map((est) => {
          // Calculate basic totals from product data
          const products = est.repeatedData || [];
          const totalDiscount = products.reduce((sum, product) => sum + (parseFloat(product.discount) || 0), 0);
          const totalFestivalDiscount = products.reduce((sum, product) => sum + (parseFloat(product.fest_discount) || 0), 0);
          
          return {
            date: est.uniqueData.created_at,
            estimate_number: est.uniqueData.estimate_number,
            invoice_number: est.repeatedData[0]?.invoice_number || "",
            product_names: products.map((p) => p.product_name).join(", "),
            net_amount: est.uniqueData.net_amount,
            // Calculate financial details from product data
            discount_amount: totalDiscount,
            festival_discount: totalFestivalDiscount,
            // These fields will be populated by detailed API calls
            scheme_amount: 0,
            old_items_amount: 0,
            sales_return_amount: 0,
            net_payable_amount: est.uniqueData.net_amount, // Use net_amount as fallback
            loading_details: true, // Flag to indicate we're loading detailed data
          };
        });

      setRows(basicTableRows);

      // Now fetch detailed data for each estimate
      const detailedRows = await Promise.all(
        basicTableRows.map(async (row) => {
          try {
            // Fetch full estimate details
            const detailRes = await axios.get(`${baseURL}/Estimate/full-details/${row.estimate_number}`);
            const estimateData = detailRes.data.estimate || detailRes.data.uniqueData || {};
            
            // Fetch URD purchase data
            let urdAmount = 0;
            try {
              const urdRes = await axios.get(`${baseURL}/get/olditems/${row.estimate_number}`);
              if (urdRes.data && Array.isArray(urdRes.data)) {
                urdAmount = urdRes.data.reduce((sum, item) => sum + (parseFloat(item.total_amount) || 0), 0);
              }
            } catch (e) {
              console.log(`No URD data for ${row.estimate_number}`);
            }

            // Fetch sales return data
            let salesReturnAmount = 0;
            try {
              const srRes = await axios.get(`${baseURL}/get-estimate-sale-returns/${row.estimate_number}`);
              if (srRes.data && Array.isArray(srRes.data)) {
                salesReturnAmount = srRes.data.reduce((sum, item) => {
                  const totalPrice = parseFloat(item.total_price) || 0;
                  const taxAmt = parseFloat(item.tax_amt) || 0;
                  const taxable = parseFloat(item.salesTaxableAmount || item.sales_taxable_amt || item.piece_taxable_amt || item.taxable_amount || 0);
                  const hm = parseFloat(item.hm_charges) || 0;
                  const net = totalPrice > 0 ? totalPrice - hm : (taxable + taxAmt) - hm;
                  return sum + net;
                }, 0);
              }
            } catch (e) {
              console.log(`No sales return data for ${row.estimate_number}`);
            }

            return {
              ...row,
              scheme_amount: parseFloat(estimateData.scheme_amount) || 0,
              old_items_amount: urdAmount,
              sales_return_amount: salesReturnAmount,
              net_payable_amount: parseFloat(estimateData.net_payable_amt) || row.net_amount,
              loading_details: false,
            };
          } catch (error) {
            console.error(`Error fetching details for ${row.estimate_number}:`, error);
            return {
              ...row,
              loading_details: false,
            };
          }
        })
      );

      // Sort the detailed rows
      const sortedRows = detailedRows.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateB.getTime() - dateA.getTime();
        }
        const aNum = parseInt((a.estimate_number || "").replace(/[^\d]/g, ""), 10) || 0;
        const bNum = parseInt((b.estimate_number || "").replace(/[^\d]/g, ""), 10) || 0;
        return bNum - aNum;
      });

      setRows(sortedRows);
    } catch (err) {
      console.error("Error fetching summary:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(async (estimate_number) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to edit this record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, go ahead!",
      cancelButtonText: "No, cancel",
    });

    if (!result.isConfirmed) return;

    // Navigate to V2 form, which will load existing estimate by number
    navigate("/estimates/v2/", { state: { estimateNumber: estimate_number } });
  }, [navigate]);

  const handleDelete = useCallback(async (estimateNumber) => {
    // Find the estimate to check its date
    const estimate = rows.find(row => row.estimate_number === estimateNumber);
    if (estimate) {
      const estimateDate = new Date(estimate.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      if (estimateDate < today) {
        Swal.fire({
          title: "Cannot Delete",
          text: "Estimates older than today cannot be deleted.",
          icon: "warning",
          confirmButtonText: "OK"
        });
        return;
      }
    }

    Swal.fire({
      title: "Are you sure?",
      text: `Do you really want to delete estimate ${estimateNumber}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(`${baseURL}/Estimate/${estimateNumber}`, { method: "DELETE" });
          if (!response.ok) throw new Error("Failed to delete estimate");
          fetchData();
          Swal.fire("Deleted!", "The estimate has been deleted.", "success");
        } catch (error) {
          console.error("Error deleting estimate:", error.message);
          Swal.fire("Error!", "Failed to delete the estimate.", "error");
        }
      }
    });
  }, [fetchData, rows]);

  const handleView = async (estimate_number) => {
    try {
      const res = await axios.get(`${baseURL}/Estimate/summary`);
      const allEstimates = res.data.summary || [];
      const matchedEstimate = allEstimates.find((est) => est.uniqueData.estimate_number === estimate_number);
      if (!matchedEstimate) {
        Swal.fire("Not Found", `Estimate ${estimate_number} not found.`, "warning");
        return;
      }
      setDetail(matchedEstimate);
      setShowModal(true);
    } catch (err) {
      console.error("Error loading estimate summary:", err);
      Swal.fire("Error", "Could not load estimate details.", "error");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setDetail(null);
  };

  /* ---------------------------- fetch URD purchase data ---------------------------- */
  const fetchUrdPurchaseData = useCallback(async (customerMobile, customerName, estimateNumber = null) => {
    try {
      console.log("Fetching URD purchase data for:", { customerMobile, customerName, estimateNumber });
      
      // Use the same API as the estimate edit screen
      if (estimateNumber) {
        console.log("Fetching URD purchase data by estimate number:", estimateNumber);
        const response = await axios.get(`${baseURL}/get/olditems/${estimateNumber}`);
        console.log("URD purchase API response:", response.data);
        
        if (response.data && Array.isArray(response.data)) {
          const urdPurchases = response.data;
          const totalAmount = urdPurchases.reduce((sum, item) => {
            const amount = parseFloat(item.total_amount || 0);
            console.log(`URD Purchase item: ${amount}`);
            return sum + amount;
          }, 0);
          
          console.log("Total URD purchase amount calculated:", totalAmount);
          
          setUrdPurchaseData(urdPurchases);
          setUrdPurchaseAmount(totalAmount);
          
          return { purchases: urdPurchases, totalAmount };
        } else {
          console.log("No URD purchase data found for estimate:", estimateNumber);
          setUrdPurchaseData([]);
          setUrdPurchaseAmount(0);
          return { purchases: [], totalAmount: 0 };
        }
      } else {
        console.log("No estimate number provided, cannot fetch URD purchase data");
        setUrdPurchaseData([]);
        setUrdPurchaseAmount(0);
        return { purchases: [], totalAmount: 0 };
      }
    } catch (error) {
      console.error("Error fetching URD purchase data:", error);
      setUrdPurchaseData([]);
      setUrdPurchaseAmount(0);
      return { purchases: [], totalAmount: 0 };
    }
  }, []);

  /* ---------------------------- fetch sales return data ---------------------------- */
  const fetchSalesReturnData = useCallback(async (customerMobile, customerName, estimateNumber = null) => {
    try {
      console.log("Fetching sales return data for:", { customerMobile, customerName, estimateNumber });
      
      if (!estimateNumber) {
        console.log("No estimate number provided, cannot fetch sales return data");
        setSalesReturnData([]);
        setSalesReturnAmount(0);
        return;
      }

      // Use the same API as V1 - get-estimate-sale-returns
      console.log("Fetching sales return data by estimate number:", estimateNumber);
      const response = await axios.get(`${baseURL}/get-estimate-sale-returns/${estimateNumber}`);
      console.log("Sales return API response:", response.data);
      
      if (response.data && Array.isArray(response.data)) {
        const salesReturns = response.data;
        const totalAmount = salesReturns.reduce((sum, item) => {
          const amount = parseFloat(item.total_price || 0);
          console.log(`Sales Return item: ${amount}`);
          return sum + amount;
        }, 0);
        
        console.log("Total sales return amount calculated:", totalAmount);
        
        setSalesReturnData(salesReturns);
        setSalesReturnAmount(totalAmount);
      } else {
        console.log("No sales return data found for estimate:", estimateNumber);
        setSalesReturnData([]);
        setSalesReturnAmount(0);
      }
    } catch (error) {
      console.error("Error fetching sales return data:", error);
      setSalesReturnData([]);
      setSalesReturnAmount(0);
    }
  }, []);

  /* ---------------------------- handle direct print ---------------------------- */
  const handleDirectPrint = useCallback(async (estimateNumber) => {
    try {
      console.log("Starting direct print for estimate:", estimateNumber);
      setOverlayLoading(true);
      
      // Fetch full estimate details
      const response = await axios.get(`${baseURL}/Estimate/full-details/${estimateNumber}`);
      const estimateData = response.data;
      
      console.log("Raw API response structure:", estimateData);
      console.log("Estimate data keys:", Object.keys(estimateData));
      
      if (!estimateData || (!estimateData.estimate && !estimateData.uniqueData)) {
        console.error("Invalid estimate data received");
        Swal.fire("Error", "Could not load estimate data for PDF generation.", "error");
        setOverlayLoading(false);
        return;
      }
      
      console.log("Full estimate data for PDF:", estimateData);
      
      // Set the data for PDF generation
      setDirectPrintData(estimateData);
      
      // Get customer details for URD and sales return data
      const customerMobile = estimateData.uniqueData?.mobile || estimateData.estimate?.mobile;
      const customerName = estimateData.uniqueData?.account_name || estimateData.estimate?.account_name;
      
      console.log("Customer details for URD/sales return:", { customerMobile, customerName });
      
      // Fetch URD purchase data
      await fetchUrdPurchaseData(
        customerMobile,
        customerName,
        estimateNumber
      );
      
      // Fetch sales return data
      await fetchSalesReturnData(
        customerMobile,
        customerName,
        estimateNumber
      );
      
      console.log("All data fetched, triggering PDF generation");
      
      // Trigger PDF generation after a short delay to ensure all data is set
      setTimeout(() => {
        const directPrintButton = document.getElementById("direct-print-pdf");
        if (directPrintButton) {
          directPrintButton.click();
        } else {
          console.error("Direct print button not found");
          setOverlayLoading(false);
        }
      }, 500);
      
    } catch (error) {
      console.error("Error in handleDirectPrint:", error);
      Swal.fire("Error", "Failed to generate PDF. Please try again.", "error");
      setOverlayLoading(false);
    }
  }, [fetchUrdPurchaseData, fetchSalesReturnData]);

  const createNew = () => {
    window.open("/estimates/v2", "_blank");
    // navigate("/estimates/v2", {
    //   state: { 
    //     isEstimatePage: true
    //   },
    // });
  };

  const columns = React.useMemo(
    () => [
      { Header: "Sr. No.", Cell: ({ row }) => row.index + 1 },
      {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) => (value ? new Date(value).toLocaleDateString("en-GB") : ""),
      },
      { Header: "Estimate #", accessor: "estimate_number" },
      { Header: "Invoice No.", accessor: "invoice_number" },
      { Header: "Product Names", accessor: "product_names" },
      { 
        Header: "Net Amount", 
        accessor: "net_amount",
        Cell: ({ value }) => value ? Math.round(parseFloat(value)) : "0"
      },
      { 
        Header: "Discount", 
        accessor: "discount_amount",
        Cell: ({ value }) => value ? Math.round(parseFloat(value)) : "0"
      },
      { 
        Header: "Festival Discount", 
        accessor: "festival_discount",
        Cell: ({ value }) => value ? Math.round(parseFloat(value)) : "0"
      },
      { 
        Header: "URD Purchase", 
        accessor: "old_items_amount",
        Cell: ({ value, row }) => {
          if (row.original.loading_details) return "Loading...";
          return value ? Math.round(parseFloat(value)) : "0";
        }
      },
      { 
        Header: "Sale Return", 
        accessor: "sales_return_amount",
        Cell: ({ value, row }) => {
          if (row.original.loading_details) return "Loading...";
          return value ? Math.round(parseFloat(value)) : "0";
        }
      },
      { 
        Header: "Scheme Redeem", 
        accessor: "scheme_amount",
        Cell: ({ value, row }) => {
          if (row.original.loading_details) return "Loading...";
          return value ? Math.round(parseFloat(value)) : "0";
        }
      },
      { 
        Header: "Net Payable", 
        accessor: "net_payable_amount",
        Cell: ({ value, row }) => {
          if (row.original.loading_details) return "Loading...";
          return value ? Math.round(parseFloat(value)) : "0";
        }
      },
      {
        Header: "Estimate",
        Cell: ({ row }) => {
          const estimateNumber = row.original?.estimate_number;

          // Show 'N/A' if estimate number is missing
          if (!estimateNumber) {
            return <span style={{ color: "gray" }}>N/A</span>;
          }

          return (
            <span
              style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
              onClick={() => handleDirectPrint(row.original.estimate_number)}
            >
              📝 View
            </span>
          );
        },
      },
      {
        Header: "Actions",
        Cell: ({ row }) => {
          const [generateSales, setGenerateSales] = useState(true);
          
          // Check if estimate is older than today
          const isEstimateOld = () => {
            const estimateDate = new Date(row.original.date);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Reset time to start of day
            return estimateDate < today;
          };
          
          useEffect(() => {
            const checkEstimateDetails = async () => {
              try {
                const response = await axios.get(
                  `${baseURL}/Estimate/estimate/${row.original.estimate_number}`
                );
                if (response.data.repair_details_id) {
                  setGenerateSales(false);
                } else {
                  setGenerateSales(true);
                }
              } catch (error) {
                console.error("Error fetching estimate details:", error);
                setGenerateSales(true);
              }
            };

            checkEstimateDetails();
          }, [row.original.estimate_number]);
          
          const canDelete = generateSales && !isEstimateOld();
          
          return (
          <div style={{ display: "flex", gap: 10 }}>
            <FaEye
              style={{ cursor: "pointer", color: "green" }}
              onClick={() => handleView(row.original.estimate_number)}
            />
            <FaEdit
              style={{ cursor: "pointer", color: "blue" }}
              onClick={() => handleEdit(row.original.estimate_number)}
            />
            <FaTrash
              style={{
                cursor: canDelete ? "pointer" : "not-allowed",
                color: canDelete ? "red" : "gray",
                opacity: canDelete ? 1 : 0.5,
              }}
              onClick={() => {
                if (!canDelete) return; 
                handleDelete(row.original.estimate_number);
              }}
              title={isEstimateOld() ? "Cannot delete estimates older than today" : generateSales ? "Delete estimate" : "Cannot delete estimate with repair details"}
            />
          </div>
          )
        },
      },
    ],
    [handleDelete, handleDirectPrint, handleEdit]
  );

  const fmt = (d) => (!d ? "" : new Date(d).toLocaleDateString("en-GB"));

  return (
    <div className="main-container">
      <div className="sales-table-container">
        <Row className="mb-3">
          <Col className="d-flex justify-content-between align-items-center">
            <h3>Estimates (v2)</h3>
            <Button className="create_but" onClick={createNew} style={{ backgroundColor: "#a36e29", borderColor: "#a36e29" }}>
              + Create
            </Button>
          </Col>
        </Row>

        {loading ? <p>Loading…</p> : <DataTable columns={columns} data={rows} />}
      </div>

      {/* Overlay loading indicator for PDF generation */}
      {overlayLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 mb-0">Generating PDF...</p>
          </div>
        </div>
      )}

      {/* Direct PDF generation for "View" links */}
      {directPrintData && (
        <BlobProvider
          document={
            <EstimateA4
              formData={{
                estimate: directPrintData.estimate?.estimate_number || directPrintData.uniqueData?.estimate_number || "N/A",
                date: directPrintData.estimate?.date || directPrintData.uniqueData?.date || new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }),
                time: new Date().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                customerName: directPrintData.account?.account_name || directPrintData.uniqueData?.account_name || "",
                mobile: directPrintData.account?.mobile || directPrintData.uniqueData?.mobile || "",
                allProducts: directPrintData.repeatedData || directPrintData.products || [],
              }}
              items={(() => {
                const items = (directPrintData.repeatedData || directPrintData.products || []);
                console.log('EstimateTableV2 Debug - items before mapping:', items);
                
                const mappedItems = items.map((item) => ({
                  categoty: `${item.category || 'General'}`,
                  item: `${item.product_name || 'Product'} (${item.metal || item.metal_type || 'N/A'})`,
                  rate: item.rate || 0,
                  weight: item.total_wt || item.total_weight_av
                    ? `${item.total_wt || item.total_weight_av} GM`
                    : "0 GM",
                  vaPercent: item.va_perc || item.wastage_percent || "0",
                  mcType: item.mc_on || "MC %",
                  mcValue: item.mc_per_gram || 0,
                  mc: `${item.mc || item.making_charges || 0}`,
                  value: item.total_price || 0,
                  discount: item.discount || 0,
                  remarks: item.remarks || "",
                  festivalDiscount: item.fest_discount || 0,
                  remark_type: item.remark_type || "",
                  remark_value: item.remark_value || "",
                  tax_amt: item.tax_amt || 0,
                  purity: item.purity || "",
                  printing_purity:item.printing_purity||"",
                }));
                
                console.log('EstimateTableV2 Debug - mapped items:', mappedItems);
                return mappedItems;
              })()}
              subtotal={(() => {
                // Try to get the correct subtotal from estimate data
                const estimateData = directPrintData.estimate || directPrintData.uniqueData || {};
                console.log('EstimateTableV2 Debug - estimateData:', estimateData);
                console.log('EstimateTableV2 Debug - estimateData fields:', Object.keys(estimateData));
                
                // Try different possible field names for total amount
                const totalAmount = parseFloat(estimateData.total_amount) || 
                                  parseFloat(estimateData.taxable_amount) || 
                                  parseFloat(estimateData.gross_amount) || 
                                  parseFloat(estimateData.total_amt) || 0;
                
                console.log('EstimateTableV2 Debug - totalAmount from fields:', totalAmount);
                
                // If no total amount found, calculate from items
                if (totalAmount === 0) {
                  const calculatedSubtotal = (directPrintData.repeatedData || directPrintData.products || []).reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
                  console.log('EstimateTableV2 Debug - calculatedSubtotal from items:', calculatedSubtotal);
                  return calculatedSubtotal;
                }
                
                return totalAmount;
              })()}
              cgst={(() => {
                const estimateData = directPrintData.estimate || directPrintData.uniqueData || {};
                
                // Try different possible field names for tax amount
                const taxAmount = parseFloat(estimateData.tax_amount) || 
                                parseFloat(estimateData.tax_amt) || 
                                parseFloat(estimateData.cgst) + parseFloat(estimateData.sgst) || 0;
                
                console.log('EstimateTableV2 Debug - tax_amount for CGST:', taxAmount);
                return taxAmount / 2;
              })()}
              sgst={(() => {
                const estimateData = directPrintData.estimate || directPrintData.uniqueData || {};
                
                // Try different possible field names for tax amount
                const taxAmount = parseFloat(estimateData.tax_amount) || 
                                parseFloat(estimateData.tax_amt) || 
                                parseFloat(estimateData.cgst) + parseFloat(estimateData.sgst) || 0;
                
                console.log('EstimateTableV2 Debug - tax_amount for SGST:', taxAmount);
                return taxAmount / 2;
              })()}
              grandTotal={(() => {
                const estimateData = directPrintData.estimate || directPrintData.uniqueData || {};
                
                // Try to get the correct net amount from estimate data
                const netAmount = parseFloat(estimateData.net_amount) || 
                                parseFloat(estimateData.net_amt) || 0;
                
                console.log('EstimateTableV2 Debug - netAmount from fields:', netAmount);
                
                // If no net amount found, calculate from items
                if (netAmount === 0) {
                  const calculatedGrandTotal = (directPrintData.repeatedData || directPrintData.products || []).reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
                  console.log('EstimateTableV2 Debug - calculatedGrandTotal from items:', calculatedGrandTotal);
                  return calculatedGrandTotal;
                }
                
                return netAmount;
              })()}
              urdPurchaseData={urdPurchaseData}
              urdPurchaseAmount={urdPurchaseAmount}
              salesReturnData={salesReturnData}
              salesReturnAmount={salesReturnAmount}
              netPayableAmount={(() => {
                const estimateData = directPrintData.estimate || directPrintData.uniqueData || {};
                const netPayableAmount = parseFloat(estimateData.net_payable_amt) || 0;
                console.log('EstimateTableV2 Debug - netPayableAmount from fields:', netPayableAmount);
                return netPayableAmount;
              })()}
            />
          }
        >
        {({ blob, loading, error }) => {
          // Debug logging for PDF data
          const estimateData = directPrintData.estimate || directPrintData.uniqueData || {};
          const calculatedSubtotal = (directPrintData.repeatedData || directPrintData.products || []).reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
          const totalAmount = parseFloat(estimateData.total_amount) || parseFloat(estimateData.taxable_amount) || parseFloat(estimateData.gross_amount) || parseFloat(estimateData.total_amt) || 0;
          const netAmount = parseFloat(estimateData.net_amount) || parseFloat(estimateData.net_amt) || 0;
          const taxAmount = parseFloat(estimateData.tax_amount) || parseFloat(estimateData.tax_amt) || parseFloat(estimateData.cgst) + parseFloat(estimateData.sgst) || 0;
          
                      console.log("PDF Generation Data:", {
              formData: {
                estimate: directPrintData.estimate?.estimate_number || directPrintData.uniqueData?.estimate_number || "N/A",
                date: directPrintData.estimate?.date || directPrintData.uniqueData?.date || new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }),
                customerName: directPrintData.estimate?.account_name || directPrintData.uniqueData?.account_name || "Customer",
                mobile: directPrintData.estimate?.mobile || directPrintData.uniqueData?.mobile || "N/A",
              },
            items: (directPrintData.repeatedData || directPrintData.products || []).length,
            calculatedSubtotal,
            totalAmount,
            netAmount,
            taxAmount,
            cgst: taxAmount / 2,
            sgst: taxAmount / 2,
            urdPurchaseAmount,
            salesReturnAmount,
            urdPurchaseDataLength: urdPurchaseData.length,
            salesReturnDataLength: salesReturnData.length
          });
          
          const handleDirectPrintPDF = () => {
            if (blob) {
              const blobUrl = URL.createObjectURL(blob);
              const newWindow = window.open(blobUrl, "_blank");

              if (newWindow) {
                // Wait for the PDF to load before triggering print
                newWindow.onload = () => {
                  newWindow.focus();
                  newWindow.print();
                  setOverlayLoading(false); // Hide overlay when PDF opens
                };
                // Fallback: Use setTimeout in case onload doesn't fire
                setTimeout(() => {
                  setOverlayLoading(false); // Hide overlay after 2 seconds as fallback
                }, 2000);
              } else {
                console.error("Failed to open new window. Please allow pop-ups.");
                alert("Please allow pop-ups to view and print the PDF.");
                setOverlayLoading(false); // Hide overlay if popup blocked
              }
              
              // Clear the direct print data after generating PDF
              setTimeout(() => {
                setDirectPrintData(null);
              }, 1000);
            } else {
              console.error("Blob is not available.");
              alert("Failed to generate PDF. Please try again.");
            }
          };

          return (
            <button
              id="direct-print-pdf"
              className="btn btn-primary d-none"
              onClick={handleDirectPrintPDF}
              disabled={loading || error}
            >
              {loading ? "Generating..." : "Direct Print PDF"}
            </button>
          );
        }}
        </BlobProvider>
      )}

      <Modal show={showModal} onHide={closeModal} size="xl" className="m-auto">
        <Modal.Header closeButton>
          <Modal.Title>Estimate Details</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '13px' }}>
          {detail && detail.uniqueData && detail.repeatedData ? (
            <>
              <h5>Estimate Info</h5>
              <Table bordered hover responsive>
                <tbody>
                  <tr>
                    <td style={{ color: '#6B451E', backgroundColor: '#f4f5f7', fontWeight: '600' }}>Date</td>
                    <td>{fmt(detail.uniqueData.date)}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#6B451E', backgroundColor: '#f4f5f7', fontWeight: '600' }}>Estimate #</td>
                    <td>{detail.uniqueData.estimate_number}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#6B451E', backgroundColor: '#f4f5f7', fontWeight: '600' }}>Customer</td>
                    <td>
                      {detail.uniqueData.account_name || "N/A"}
                      {detail.uniqueData.mobile && ` (${detail.uniqueData.mobile})`}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#6B451E', backgroundColor: '#f4f5f7', fontWeight: '600' }}>Total Amount</td>
                    <td>{detail.uniqueData.total_amount || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#6B451E', backgroundColor: '#f4f5f7', fontWeight: '600' }}>Discount Amount</td>
                    <td>{detail.uniqueData.discount_amount || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#6B451E', backgroundColor: '#f4f5f7', fontWeight: '600' }}>Festival Discount</td>
                    <td>{detail.uniqueData.festival_discount || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#6B451E', backgroundColor: '#f4f5f7', fontWeight: '600' }}>URD Purchase Amount</td>
                    <td>{detail.uniqueData.old_items_amount || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#6B451E', backgroundColor: '#f4f5f7', fontWeight: '600' }}>Sale Return Amount</td>
                    <td>{detail.uniqueData.sales_return_amount || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#6B451E', backgroundColor: '#f4f5f7', fontWeight: '600' }}>Scheme Amount</td>
                    <td>{detail.uniqueData.scheme_amount || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#6B451E', backgroundColor: '#f4f5f7', fontWeight: '600' }}>Net Payable Amount</td>
                    <td>{detail.uniqueData.net_payable_amount || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#6B451E', backgroundColor: '#f4f5f7', fontWeight: '600' }}>Net Amount</td>
                    <td>{detail.uniqueData.net_amount}</td>
                  </tr>
                </tbody>
              </Table>

              <h5>Products</h5>
              <div className="table-responsive">
                <Table bordered hover responsive>
                  <thead style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                    <tr>
                      <th style={{ color: '#6B451E' }}>Barcode</th>
                      <th style={{ color: '#6B451E' }}>Product</th>
                      <th style={{ color: '#6B451E' }}>Metal</th>
                      <th style={{ color: '#6B451E' }}>Purity</th>
                      <th style={{ color: '#6B451E' }}>Gr Wt</th>
                      <th style={{ color: '#6B451E' }}>St Wt</th>
                      <th style={{ color: '#6B451E' }}>Total Wt</th>
                      <th style={{ color: '#6B451E' }}>Rate</th>
                      <th style={{ color: '#6B451E' }}>MC Type</th>
                      <th style={{ color: '#6B451E' }}>MC Value</th>
                      <th style={{ color: '#6B451E' }}>MC Amount</th>
                      <th style={{ color: '#6B451E' }}>Discount</th>
                      <th style={{ color: '#6B451E' }}>Tax Amt</th>
                      <th style={{ color: '#6B451E' }}>Total Price</th>
                    </tr>
                  </thead>
                  <tbody style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                    {detail.repeatedData.map((p, i) => (
                      <tr key={i}>
                        <td>{p.barcode}</td>
                        <td>{p.product_name}</td>
                        <td>{p.metal}</td>
                        <td>{p.purity}</td>
                        <td>{p.gr_wt}</td>
                        <td>{p.st_wt}</td>
                        <td>{p.total_wt}</td>
                        <td>{p.rate}</td>
                        <td>{p.mc_on || "MC %"}</td>
                        <td>{p.mc_per_gram || 0}</td>
                        <td>{p.mc}</td>
                        <td>{p.discount ?? "-"}</td>
                        <td>{p.tax_amt}</td>
                        <td>{p.total_price}</td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 'bold' }}>
                      <td colSpan="13" className="text-end">
                        Net Amount
                      </td>
                      <td>{detail.uniqueData.net_amount}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </>
          ) : (
            <p>Loading details or invalid estimate data.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default EstimateTableV2;